import { useState, useEffect, useContext } from 'react';
import {
  FormControlLabel,
  Switch,
  Button,
  Divider,
  Box,
  useMediaQuery,
  Checkbox,
  Tooltip
} from '@mui/material';
import { OrderManagementPage, PageTitle, Table, Pagination } from '../../../components';
import { AssignmentLate, PublishedWithChanges, Visibility } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { IChecklistReview } from '../../../models';
import { ChecklistReviewFilters } from './ChecklistReviewFilters';
import { ChecklistReviewMobile } from './ChecklistReviewMobile';
import {
  getPlaceOrderChecklistReviews,
  getPlaceOrderChecklistReviewsArchive,
  processPurchaseOrderChecklist,
  getAllPurchaseOrderChecklists
} from '../../../fetch';
import { checklistsParentLink, formatDate, unescape } from '../../../helpers';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { UserContext } from '../../../context';

export const ChecklistReview = () => {
  const { isBillingAdmin, isFinancialAdmin, isAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const classes = useStyles();
  const currentQueryString = window.location.search;
  const [queryString, setQueryString] = useState('');
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [selectedJob, setSelectedJob] = useState<string | number>('');
  const [selectedType, setSelectedType] = useState<string>('null');
  const [checklistReviews, setChecklistReviews] = useState<IChecklistReview[]>([]);
  const [isLoadingReviews, setLoadingChecklistReviews] = useState<boolean>(false);
  const [isArchivedChecklists, setArchievedChecklists] = useState<boolean>(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filters, setFilters] = useState<{
    isOpenEnded?: string;
    jobSageId?: string | number;
    completedOnEndDate?: string;
  } | null>({});
  const [completedOnDate, setCompletedOnDate] = useState<any>(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const [isLoadingAllPurchaseOrderChecklists, setLoadingAllPurchaseOrderChecklists] =
    useState<boolean>(false);
  const [isAllSelected, setSelectedAll] = useState<boolean>(false);
  const [selectedChecklists, setSelectedChecklists] = useState<any>([]);
  const [isProcessingChecklists, setIsProcessingChecklist] = useState<boolean>(false);
  const canProcessPOs = isAdmin || isBillingAdmin || isFinancialAdmin;

  const updateUrlWithFilters = newFilters => {
    const queryParams = new URLSearchParams();
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        queryParams.set(key, newFilters[key]);
      }
    });
    const newQueryString = queryParams.toString();
    setQueryString(newQueryString);
    history.push({ search: newQueryString });
  };

  const fetchChecklistReviews = async () => {
    let formattedDate = '';
    if (completedOnDate) {
      formattedDate = formatDate(completedOnDate);
    }

    setLoadingChecklistReviews(true);
    if (currentQueryString && Object.keys(filters).length === 0) {
      return;
    }
    try {
      let res = null;
      if (isArchivedChecklists) {
        res = await getPlaceOrderChecklistReviewsArchive({
          page: page + 1,
          perPage,
          sortBy: 'OrderNumber',
          sortDirection: 'Desc',
          completedOnEndDate: formattedDate || null,
          ...filters
        });
      } else {
        res = await getPlaceOrderChecklistReviews({
          page: page + 1,
          perPage,
          sortBy: 'OrderNumber',
          sortDirection: 'Desc',
          completedOnEndDate: formattedDate || null,
          ...filters,
          isOpenEnded: selectedType === 'null' ? null : selectedType === 'true' ? true : false
        });
      }
      setChecklistReviews(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
    } finally {
      setLoadingChecklistReviews(false);
    }
  };

  const processPOChecklists = async () => {
    setIsProcessingChecklist(true);
    try {
      if (!isArchivedChecklists && selectedChecklists.length > 0) {
        await processPurchaseOrderChecklist({
          purchaseOrderCheckListIds: selectedChecklists
        });
        enqueueSnackbar('Selected checklists successfully processed!', {
          variant: 'success'
        });
      }
      return;
    } catch (error) {
      enqueueSnackbar(
        error?.Detail ?? `Error processing checklists. Please refresh and try again.`,
        {
          variant: 'error'
        }
      );
    } finally {
      setIsProcessingChecklist(false);
      setSelectedChecklists([]);
      setSelectedAll(false);
      fetchChecklistReviews();
    }
  };

  useEffect(() => {
    if (!isLoadingReviews) return;

    const queryParams = new URLSearchParams(window.location.search);
    const urlFilters: any = {};

    queryParams.forEach((value, key) => {
      urlFilters[key] = value;
    });

    if (Object.keys(urlFilters).length > 0) {
      const newFilters: any = {};

      if (urlFilters.jobSageId) {
        newFilters.jobSageId = urlFilters.jobSageId;
        setSelectedJob(Number(urlFilters.jobSageId));
      }
      if (urlFilters.completedOnEndDate) {
        const dateTime = new Date(urlFilters.completedOnEndDate);
        newFilters.completedOnEndDate = dateTime;
        setCompletedOnDate(dateTime);
      }
      if (urlFilters.isOpenEnded) {
        newFilters.isOpenEnded = urlFilters.isOpenEnded;
        setSelectedType(urlFilters.isOpenEnded === 'true' ? 'true' : 'false');
      }

      setFilters(prevFilters => ({
        ...prevFilters,
        ...newFilters
      }));

      updateUrlWithFilters(newFilters);
      setHasAppliedFilters(Object.keys(newFilters).length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingReviews]);

  useEffect(() => {
    fetchChecklistReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, perPage, isArchivedChecklists]);

  const handleEdit = async (id: string) => {
    history.push(`/production/checklists/checklist-review/${id}?${queryString}`);
  };

  const columns = [
    !isArchivedChecklists &&
      canProcessPOs && {
        id: 'checkbox',
        accessor: '',
        Header: '',
        overrideWidth: 50,
        sort: false,
        Cell: ({ cell }) => {
          const checklist: IChecklistReview = cell.row.original;
          const ids = selectedChecklists;
          const isSelected = ids.includes(checklist.purchaseOrderChecklistId);
          const showCheckbox = checklist.status === 'Meets Standards';
          const showWarning = checklist.status !== 'Meets Standards';

          if (showWarning) {
            return (
              <Tooltip
                title={`This checklist is not available for bulk processing due to its status of: ${checklist.status}`}
              >
                <AssignmentLate />
              </Tooltip>
            );
          } else {
            return (
              showCheckbox && (
                <FormControlLabel
                  classes={{
                    label: classes.tableCheckbox
                  }}
                  color="primary"
                  control={
                    <Checkbox
                      checked={isSelected}
                      id="checklists-checkbox"
                      disabled={isLoadingReviews || isProcessingChecklists}
                      onChange={() => {
                        if (isSelected) {
                          // If the item is already selected, remove it from the array
                          setSelectedChecklists(
                            selectedChecklists.filter(
                              id => id !== checklist.purchaseOrderChecklistId
                            )
                          );
                        } else {
                          // If the item is not selected, add it to the array
                          setSelectedChecklists([
                            ...selectedChecklists,
                            checklist.purchaseOrderChecklistId
                          ]);
                        }
                      }}
                    />
                  }
                  label=""
                />
              )
            );
          }
        }
      },
    {
      Header: 'Order Number',
      accessor: 'orderNumber',
      sort: false
    },
    {
      Header: 'Vendor',
      accessor: 'vendorName',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IChecklistReview } };
      }) => {
        return <>{unescape(original.vendorName)}</>;
      }
    },
    {
      Header: 'Job',
      accessor: 'jobForeignId',
      sort: false
    },
    {
      Header: 'Task',
      accessor: 'taskName',
      sort: false
    },
    {
      Header: 'Type',
      accessor: 'typeCode',
      sort: false
    },
    {
      Header: 'Status',
      accessor: 'status',
      sort: false
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IChecklistReview } };
      }) => {
        return original.poExists ? (
          <Box textAlign="right">
            <Button
              id="edit"
              color="primary"
              startIcon={<Visibility />}
              onClick={() => {
                handleEdit(original.purchaseOrderForeignId);
              }}
            >
              View Details
            </Button>
          </Box>
        ) : (
          <></>
        );
      }
    }
  ].filter(Boolean);

  return (
    <OrderManagementPage
      title="Checklist Review"
      requiredFeature={checklistsParentLink.featureFlag}
      legacyUrl={checklistsParentLink.legacyUrl}
    >
      <Box display="flex" flexWrap="wrap">
        <Box
          sx={{
            '@media (min-width: 768px)': {
              flex: 1
            }
          }}
        >
          <PageTitle showDivider={false} title="Checklist Review" />
        </Box>
        <Box
          sx={{
            width: '100%',
            marginBottom: '1rem',
            '@media (min-width: 768px)': {
              textAlign: 'right',
              width: 'auto',
              marginBottom: 0
            }
          }}
        >
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={isArchivedChecklists}
                id="archived"
                disabled={isLoadingReviews || isProcessingChecklists}
                onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  // reset these if the filter for "Archived" changes
                  setSelectedJob('');
                  setHasAppliedFilters(false);
                  setSelectedType('null');
                  setRowsPerPage(10);
                  setPage(0);
                  setFilters({});
                  setArchievedChecklists(checked);
                  setSelectedChecklists([]);
                  setCompletedOnDate(null);
                }}
              />
            }
            label="Archived"
          />
        </Box>
      </Box>
      <Divider />
      <ChecklistReviewFilters
        isLoading={isLoadingReviews}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          let newFilters = {};
          if (clearFilters) {
            newFilters = {
              ...filters,
              jobSageId: '',
              isOpenEnded: null,
              completedOnEndDate: null
            };
            setSelectedChecklists([]);
            setSelectedAll(false);
            setSelectedJob('');
            setSelectedType('null');
            setCompletedOnDate(null);
          } else {
            setPage(0);
            if (selectedType === 'null') {
              newFilters = {
                ...filters,
                jobSageId: selectedJob || undefined,
                completedOnEndDate: completedOnDate
              };
            } else {
              newFilters = {
                ...filters,
                jobSageId: selectedJob || undefined,
                isOpenEnded: selectedType || null,
                completedOnEndDate: completedOnDate
              };
            }
            setSelectedChecklists([]);
            setSelectedAll(false);
          }
          setFilters(newFilters);
          updateUrlWithFilters(newFilters);
        }}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedJob={selectedJob as string}
        setSelectedJob={setSelectedJob}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        isArchivedChecklists={isArchivedChecklists}
        completedOnDate={completedOnDate}
        setCompletedOnDate={setCompletedOnDate}
        setSelectedChecklists={setSelectedChecklists}
      />

      {!isArchivedChecklists && canProcessPOs && (
        <>
          <Divider />
          <div className={classes.actionsWrapper}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllSelected}
                  id="checklists-checkbox"
                  disabled={
                    isLoadingReviews ||
                    isProcessingChecklists ||
                    isLoadingAllPurchaseOrderChecklists
                  }
                  onChange={async () => {
                    if (isAllSelected) {
                      setSelectedChecklists([]);
                    } else {
                      try {
                        setLoadingAllPurchaseOrderChecklists(true);
                        const allChecklists = await getAllPurchaseOrderChecklists({
                          completedOnEndDate: formatDate(completedOnDate),
                          ...filters,
                          isOpenEnded:
                            selectedType === 'null' ? null : selectedType === 'true' ? true : false
                        });
                        setSelectedChecklists(allChecklists);
                      } catch (error) {
                      } finally {
                        setLoadingAllPurchaseOrderChecklists(false);
                      }
                    }
                    setSelectedAll(!isAllSelected);
                  }}
                />
              }
              label=""
            />
            <LoadingButton
              id={'process_POs'}
              color="primary"
              variant="contained"
              disabled={isLoadingReviews || isProcessingChecklists || selectedChecklists.length < 1}
              onClick={() => {
                const confirmMessage = `Are you sure you want to process ${selectedChecklists.length} Purchase Order(s)?`;
                if (window.confirm(confirmMessage)) {
                  processPOChecklists();
                }
              }}
              startIcon={<PublishedWithChanges />}
              loading={isProcessingChecklists}
            >
              Process PO's
            </LoadingButton>
          </div>
          <Divider />
        </>
      )}

      <Table
        columns={columns}
        data={checklistReviews}
        isLoading={isLoadingReviews}
        hidePagination
        ResponsiveComponent={isMobile ? ChecklistReviewMobile : undefined}
        mobileProps={{
          handleEdit: (val: IChecklistReview) => handleEdit(val.purchaseOrderForeignId),
          selectedChecklists,
          setSelectedChecklists: val => setSelectedChecklists(val),
          isArchivedChecklists,
          canProcessPOs
        }}
      />
      {!isLoadingReviews && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </OrderManagementPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionsWrapper: {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center'
  },
  tableCheckbox: {
    marginRight: 0
  }
}));
